// src/pages/PrincipalRegistration.js
// v1.3 — polished UI, same behaviour

import React, { useState, useEffect } from "react";
import axios from "axios";
import "../styles/PrincipalRegistration.css";

const PrincipalRegistration = () => {
  const [rollNumber, setRollNumber] = useState("");
  const [schoolData, setSchoolData] = useState(null);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [principalEmail, setPrincipalEmail] = useState("");
  const [isRegistered, setIsRegistered] = useState(false);

  // Smoothly scroll to top whenever a message appears (success/error)
  useEffect(() => {
    if (message) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [message]);

  const fetchSchoolData = async (cleanedRollNumber) => {
    try {
      const response = await axios.get(
        `https://ghrianghraffy.com/auth/get-school-data/${cleanedRollNumber}`
      );
      setSchoolData(response.data);
      setPrincipalEmail(response.data.principal_email);
      setEmail(response.data.principal_email);
      setMessage("");
    } catch (error) {
      setSchoolData(null);
      setPrincipalEmail("");
      setEmail("");
      setMessage("School not found. Please check the Roll Number.");
    }
  };

  const handleRegister = async () => {
    // Scroll immediately on click so the user is brought to the message area
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (!/\S+@\S+\.\S+/.test(email)) {
      setMessage("Invalid email format. Please enter a valid email address.");
      return;
    }

    try {
      const response = await axios.post(
        "https://ghrianghraffy.com/auth/principal-registration",
        {
          email,
          role: "PRINCIPAL",
          roll_number: rollNumber.trim(),
        }
      );
      setMessage(response.data.message);
      setIsRegistered(true);
    } catch (error) {
      setMessage(
        error.response?.data?.detail || "Failed to register. Please try again."
      );
    }
  };

  return (
    <div className="principal-registration page">
      <div className="registration-card">
        <header className="registration-header">
          <h1 className="registration-title">
            Principal Registration
          </h1>
          <p className="registration-subtitle">
            Start your free 7-day trial — no card required. We’ll match your
            school by Roll Number and send your login link by email.
          </p>
        </header>

        {message && (
          <p
            className={`message ${
              message.includes("Failed") ? "error" : "success"
            }`}
            aria-live="polite"
          >
            {message}
          </p>
        )}

        <section className="registration-section">
          <h2 className="section-title">1. Find your school</h2>
          <div className="form-group">
            <label htmlFor="roll-number">Roll Number</label>
            <input
              id="roll-number"
              type="text"
              className="input-field"
              placeholder="Enter your school’s Roll Number"
              value={rollNumber}
              onChange={(e) => {
                const cleaned = e.target.value.trim();
                setRollNumber(cleaned);
                if (cleaned.length >= 5) {
                  fetchSchoolData(cleaned);
                } else {
                  setSchoolData(null);
                  setPrincipalEmail("");
                  setEmail("");
                  setMessage("");
                }
              }}
              onBlur={(e) => {
                setRollNumber(e.target.value.trim());
              }}
            />
          </div>

          {schoolData && (
            <div className="school-details">
              <p>
                <strong>School Name:</strong> {schoolData.official_name}
              </p>
              <p>
                <strong>Address:</strong> {schoolData.address_line_1},{" "}
                {schoolData.county_description}
              </p>
              <p>
                <strong>Eircode:</strong> {schoolData.eircode}
              </p>
              <p>
                <strong>Phone:</strong> {schoolData.phone_no}
              </p>
              <p>
                <strong>Principal:</strong> {schoolData.principal_name}</p>
              <p>
                <strong>Principal Email:</strong> {principalEmail}
              </p>
              <p>
                <strong>Enrolment:</strong> {schoolData.enrolment_quantity}
              </p>
              {schoolData.school_website && (
                <p>
                  <strong>School Website:</strong>{" "}
                  <a
                    href={schoolData.school_website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="school-website-link"
                  >
                    {schoolData.school_website}
                  </a>
                </p>
              )}
              {schoolData.website_tech_used && (
                <p>
                  <strong>Website Technology:</strong>{" "}
                  {schoolData.website_tech_used}
                </p>
              )}
            </div>
          )}
        </section>

        <section className="registration-section">
          <h2 className="section-title">2. Confirm your email</h2>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <div className="input-wrapper">
              <input
                id="email"
                type="email"
                className="input-field"
                placeholder="Email for your GhrianGhraffy account"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <span className="tooltip">
                This email will be used for your GhrianGhraffy principal
                account. You can overwrite the suggested address if another
                inbox suits you better.
              </span>
            </div>
          </div>
        </section>

        <button
          className="register-button"
          onClick={handleRegister}
          disabled={!rollNumber || !email || !schoolData || isRegistered}
          title={
            isRegistered
              ? "You are already registered."
              : !rollNumber || !email || !schoolData
                ? "Please complete all fields."
                : ""
          }
        >
          {isRegistered ? "Registered ✅" : "Register"}
        </button>
      </div>
    </div>
  );
};

export default PrincipalRegistration;
